/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactElement,
} from "react";
import {
  createBrowserRouter,
  Route,
  RouterProvider,
  Routes,
  useLocation,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { HttpError, QueryClient, QueryClientProvider } from "@superweb/api";
import { cssFns, CssInstance, useCss } from "@superweb/css";
import {
  AppContainer,
  Messages as SuperwebUiMessages,
  Theme,
  useBrandColors,
  useUiColors,
} from "@superweb/ui";
import { useLocale } from "@superweb/intl";

import { useV1ParkInfoGet } from "@fleet/api/fleet-forms";
import { Api, useQuery } from "@fleet/api";
import { useFlag } from "@superweb/flags";
import {
  ErrorBoundary,
  Messages as FleetUiMessages,
  Unexpected,
  YangoTheme,
} from "@fleet/ui";

import {
  Navigate,
  searchParamTypes as t,
  useSearchParams,
} from "#internal/router-new";

import { Form } from "./form";
import { Flags } from "./flags";
import { Messages } from "./intl";
import { AppLocale } from "./app-locale";
import { Errors } from "./errors";
import { NotFound, OperationId, useOperationIdContext } from "./ui";
import { useLogHit } from "./analytics/logActions";
import { initGtag } from "./analytics/gtag";
import { initFBQ } from "./analytics/fbq";
import { initTTQ } from "./analytics/ttq";
import "./analytics/initYM";
import faviconYango from "./images/favicon-yango.png";
import faviconYandex from "./images/favicon-yandex.png";
import { ArchivePage } from "./form/archive";
import { PageSpinner } from "./ui";
import { authWithPassport } from "./auth";
import { ColorSchemeProvider } from "./ui/color-scheme-provider";
import { RegisterForm } from "./form/register";
import { VehicleForm } from "./form/vehicle";
import { DetailsForm } from "./form/details";
import { EmploymentTypeForm } from "./form/employment-type";
import { ProfessionForm } from "./form/profession";
import { DownloadAppPage } from "./form/download-app-page";
import { LicenseForm } from "./form/license";
import { useReportWebVitals } from "./analytics";
import { ParkInfoContextProvider } from "./park-info-context";

const queryClient = new QueryClient();

const useLogLocation = (logHit: (url: string) => void) => {
  const location = useLocation();
  const prevPathname = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname !== prevPathname.current) {
      prevPathname.current = location.pathname;
      logHit(`${location.pathname}${location.search}`);
    }
  }, [location.pathname, location.search, logHit]);
};

const AppContainerWrapper = ({ children }: { children: ReactElement }) => {
  const css = useCss();
  const brandColors = useBrandColors();
  const locale = useLocale();

  const bodyClassName = css({
    color: brandColors.text,
    backgroundColor: brandColors.background,
    ...cssFns.margin("0"),
  });

  useLayoutEffect(() => {
    const list = bodyClassName.split(" ");
    document.body.classList.add(...list);
    return () => {
      document.body.classList.remove(...list);
    };
  }, [bodyClassName]);

  useLayoutEffect(() => {
    document.body.dir = locale.textInfo.direction;
  }, [locale.textInfo.direction]);

  return <AppContainer>{children}</AppContainer>;
};

const Root = () => {
  return (
    <AppContainerWrapper>
      <OperationId>
        <ErrorBoundary
          fallback={({ error, traceId, clientDebugId, reset }) => {
            if (error instanceof HttpError && error.status === 403) {
              authWithPassport();

              return <PageSpinner />;
            }

            return (
              <div
                css={{ height: "100vh", display: "flex", alignItems: "center" }}
              >
                <Unexpected
                  traceId={traceId}
                  clientDebugId={clientDebugId}
                  reset={reset}
                />
              </div>
            );
          }}
        >
          <Suspense fallback={<PageSpinner />}>
            <SuperwebUiMessages>
              <FleetUiMessages>
                <Messages>
                  <Errors>
                    <AppContent />
                  </Errors>
                </Messages>
              </FleetUiMessages>
            </SuperwebUiMessages>
          </Suspense>
        </ErrorBoundary>
      </OperationId>
    </AppContainerWrapper>
  );
};

const router = createBrowserRouter([{ path: "*", Component: Root }]);

export const App = () => {
  const yangoBrand = window.location.hostname.split(".").includes("yango");

  return (
    <Flags>
      <CssInstance
        nonce={
          (
            document.querySelector(
              "meta[property=csp-nonce]",
            ) as HTMLMetaElement | null
          )?.nonce
        }
      >
        <QueryClientProvider client={queryClient}>
          <Api
            options={{
              enableDefaultStaleTime: Boolean(
                import.meta.env.FLEET_FORMS_PARK_INFO_FROM_CONTEXT,
              ),
            }}
          >
            <AppLocale>
              <HelmetProvider>
                <ColorSchemeProvider>
                  <Theme value={yangoBrand ? YangoTheme : undefined}>
                    <RouterProvider router={router} />
                  </Theme>
                </ColorSchemeProvider>
              </HelmetProvider>
            </AppLocale>
          </Api>
        </QueryClientProvider>
      </CssInstance>
    </Flags>
  );
};

const AppContent = () => {
  const uiColors = useUiColors();
  const locale = useLocale();
  const logHit = useLogHit();
  const location = useLocation();

  useLogLocation(logHit);
  useReportWebVitals();

  const [{ ref_id, specification, profession_id, employment_type_id }] =
    useSearchParams({
      ref_id: t.string,
      specification: t.string,
      profession_id: t.string,
      employment_type_id: t.enumString([
        "selfemployed",
        "individual_entrepreneur",
        "park_employee",
      ] as const),
    });

  const isIframe = window.self !== window.top;
  const isDelivery = specification === "delivery";

  const { sessionId } = useOperationIdContext();

  const flag = useFlag();
  const enableParkInfoFromContext = flag({
    id: "9070a312-2886-44dd-91f0-596e1a1f651f",
    description: "Enable park info from context",
  });

  const { data: parkInfoDeprecated } = useV1ParkInfoGet(
    {
      headers: {
        language: locale.toString(),
        "x-session-id": sessionId,
      },
      query: {
        ref_id: ref_id!,
        ...(profession_id && { profession_id }),
        ...(employment_type_id && { employment_type: employment_type_id }),
      },
    },
    {
      enabled: !enableParkInfoFromContext && Boolean(ref_id),
    },
  );

  const { response: parkInfoNew } = useQuery(
    enableParkInfoFromContext && ref_id
      ? {
          url: "/fleet-forms/selfreg/v1/park/info",
          method: "GET",
          headers: {
            language: locale.toString(),
            "x-session-id": sessionId,
          },
          query: {
            ref_id,
            ...(profession_id && { profession_id }),
            ...(employment_type_id && { employment_type: employment_type_id }),
          },
        }
      : undefined,
  );

  const parkInfoNormalized = parkInfoDeprecated
    ? parkInfoDeprecated.status === 200
      ? { status: 200 as const, body: parkInfoDeprecated.data }
      : parkInfoDeprecated.status === 410
        ? { status: 410 as const, body: { park_info: parkInfoDeprecated.data } }
        : { status: 400 as const, body: parkInfoDeprecated.error }
    : undefined;

  const parkInfo = enableParkInfoFromContext ? parkInfoNew : parkInfoNormalized;

  useEffect(() => {
    if (parkInfo?.status === 200) {
      switch (parkInfo.body.channel_id) {
        case "google_ads": {
          const ga_id = parkInfo.body.google_adwords_id;
          initGtag(ga_id);
          break;
        }
        case "facebook": {
          const pixel_id = parkInfo.body.fb_pixel_id;
          initFBQ(pixel_id);
          break;
        }
        case "tik_tok": {
          const tiktok_pixel_id = parkInfo.body.tiktok_pixel_id;
          initTTQ(tiktok_pixel_id);
          break;
        }
        default:
          break;
      }
    }
  }, [parkInfo]);

  if (!ref_id || !parkInfo || parkInfo.status === 400) return <NotFound />;

  const brand = parkInfo.status === 200 ? parkInfo.body.brand : undefined;

  return (
    <ParkInfoContextProvider value={parkInfoNew}>
      <Helmet>
        <link
          rel="icon"
          type="image/png"
          href={brand === "yango" ? faviconYango : faviconYandex}
          sizes="16x16"
        />
      </Helmet>
      <ColorSchemeProvider>
        <Theme value={brand === "yango" ? YangoTheme : undefined}>
          <div
            css={{
              height: "100vh",
              display: "flex",
              minWidth: "min-content",
              isolation: "isolate",
              ...(isIframe && {
                maxHeight: "600px",
                maxWidth: "600px",
              }),
              justifyContent: "center",
            }}
          >
            <div
              css={{
                flexGrow: "1",
                display: "grid",
                backgroundColor: uiColors.background,
              }}
            >
              <Suspense fallback={<PageSpinner />}>
                <Routes>
                  {parkInfo.status !== 200 ? (
                    <Route
                      path="*"
                      element={
                        <ArchivePage parkInfo={parkInfo.body.park_info} />
                      }
                    />
                  ) : parkInfo.body.passport_authorization ? (
                    <>
                      <Route
                        path="/register/*"
                        element={<RegisterForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/employment-type"
                        element={<EmploymentTypeForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/profession/*"
                        element={<ProfessionForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/license"
                        element={<LicenseForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/vehicle"
                        element={<VehicleForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/details"
                        element={<DetailsForm isIframe={isIframe} />}
                      />
                      <Route
                        path="/download-app"
                        element={<DownloadAppPage isIframe={isIframe} />}
                      />
                      <Route
                        path="/forms"
                        element={
                          <Navigate
                            to={`/register${location.search}`}
                            replace
                          />
                        }
                      />
                    </>
                  ) : (
                    <Route
                      path="/forms"
                      element={
                        <Form
                          parkInfo={parkInfo.body}
                          refId={ref_id}
                          isIframe={isIframe}
                          isDelivery={isDelivery}
                          country={parkInfo.body.country_code}
                        />
                      }
                    />
                  )}
                  <Route path="*" element={<NotFound />} />
                  <Route
                    path="/archive"
                    element={
                      <ArchivePage
                        parkInfo={
                          (
                            parkInfo.body as Extract<
                              typeof parkInfo,
                              { status: 410 }
                            >["body"]
                          ).park_info
                        }
                      />
                    }
                  />
                </Routes>
              </Suspense>
            </div>
          </div>
        </Theme>
      </ColorSchemeProvider>
    </ParkInfoContextProvider>
  );
};
